// src/components/event/EventSection.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventItem, Place } from '../../types/shared/event';
import { useHeader } from '../../context/HeaderContext';
import { CategoryFilter } from '../shared/CategoryFilter';
import { ListView } from './ListView';
import MapView from './MapView';
import { ViewToggle } from './ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import DateRangeSelector from '../event-api/DateRangeSelector';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { stayService } from '../../lib/stay/stayService';
import { navigationClient } from '../../lib/navigation';
import { eventClient } from '../../lib/event';
import { useAnalytics } from '../../hooks/useAnalytics';
import ErrorBoundary from '../analytics/ErrorBoundary';

const EventSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'event'
  });
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedItem, setSelectedItem] = useState<EventItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);
  
  // Date range state for filtering
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useState<{
    startDateTime?: string;
    endDateTime?: string;
  } | null>(null);
  const [isDateRangeSelectorModalOpen, setIsDateRangeSelectorModalOpen] = useState(true);
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [areDatesExpired, setAreDatesExpired] = useState(false);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch items using the event client
  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ['event-items', section?.id, searchParams],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }
      
      // Track section load start
      analytics.trackEvent('performance', 'section_load_start', {
        section_id: section.id,
        section_type: 'event'
      });
      
      const startTime = performance.now();
      
      // If no search params are set or dates are expired, return empty array
      if (!searchParams || areDatesExpired) {
        return [];
      }
      
      try {
        const result = await eventClient.getEvents(
          section.id, 
          searchParams?.startDateTime, 
          searchParams?.endDateTime
        );
        
        // Track section load complete
        const loadTime = performance.now() - startTime;
        analytics.trackEvent('performance', 'section_load_complete', {
          section_id: section.id,
          section_type: 'event',
          load_time: loadTime,
          item_count: result.length
        });
        
        return result;
      } catch (error) {
        // Track section load error
        analytics.trackError('section_load_error', error as Error, {
          section_id: section.id,
          section_type: 'event'
        });
        throw error;
      }
    },
    enabled: !!section?.id && !!searchParams && !areDatesExpired, // Only run query when search params are set and dates are not expired
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analytics.trackSectionView(view);
    }
  }, [section?.id, view, analytics]);

  // Category colors memo
  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, items]);

  // Filtered items memo
  const filteredItems = React.useMemo(() => {
    return items.filter(item => {
      if (selectedType) return item.type === selectedType;
      if (selectedCategory) return item.category === selectedCategory;
      return true;
    });
  }, [items, selectedCategory, selectedType]);

  // Places memo
  const places = React.useMemo(() => {
    return eventClient.getPlaces(filteredItems);
  }, [filteredItems]);

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items.length > 0) {
      // Check if the name contains date and time information
      const parts = name.split('-');
      if (parts.length >= 3) {
        // The last two parts should be date and time
        const dateStr = parts[parts.length - 2];
        const timeStr = parts[parts.length - 1];
        
        // Try to find an event with matching name, date, and time
        const event = items.find(e => {
          const eventNameSlug = slugify(e.name);
          const eventDateSlug = e.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
          const eventTimeSlug = e.hour.replace(':', ''); // Convert HH:MM to HHMM
          
          // Check if the base name matches (excluding date and time parts)
          const baseNameSlug = parts.slice(0, parts.length - 2).join('-');
          
          return eventNameSlug.includes(baseNameSlug) && 
                 eventDateSlug === dateStr && 
                 eventTimeSlug === timeStr;
        });
        
        if (event) {
          setSelectedItem(event);
          
          // Track detail card view from URL navigation
          analytics.trackItemAction(event.id, 'item_click', 'event', {
            item_name: event.name,
            category: event.category,
            type: event.type,
            source: 'url_navigation',
            date: event.date,
            time: event.hour,
            show_id: event.idShow || event.idshow
          });
          
          return;
        }
      }
      
      // Fallback to just matching by name if the above doesn't work
      const event = items.find(i => slugify(i.name) === name);
      if (event) {
        setSelectedItem(event);
        
        // Track detail card view from URL navigation
        analytics.trackItemAction(event.id, 'item_click', 'event', {
          item_name: event.name,
          category: event.category,
          type: event.type,
          source: 'url_navigation',
          date: event.date,
          time: event.hour,
          show_id: event.idShow || event.idshow
        });
      }
    }
  }, [name, items, analytics]);

  // Initialize dates from stay service
  useEffect(() => {
    if (datesInitialized) {
      return; // Skip if already initialized
    }

    // Check if dates are expired
    const datesExpired = stayService.areDatesExpired();
    setAreDatesExpired(datesExpired);
    
    // Initialize with today's date and tomorrow as default
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59);
    
    setStartDate(today);
    setEndDate(tomorrow);
    
    // Check if we have saved stay dates
    const savedDates = stayService.getStayDates();
    
    if (savedDates && !datesExpired) {
      // Use saved dates only if they're not expired
      setStartDate(new Date(savedDates.startDate));
      setEndDate(new Date(savedDates.endDate));
      
      // Auto-search with saved dates
      setSearchParams({
        startDateTime: savedDates.startDate,
        endDateTime: savedDates.endDate
      });
      
      // Close the date selector modal
      setIsDateRangeSelectorModalOpen(false);
    } else {
      // Keep the date selector modal open
      setIsDateRangeSelectorModalOpen(true);
      
      // Clear search params if dates are expired
      setSearchParams(null);
    }

    setDatesInitialized(true);
  }, [datesInitialized]);

  // Event handlers
  const handleBackClick = () => {
    analytics.trackEvent('navigation', 'menu_click', {
      target: 'home',
      section_id: section?.id,
      section_name: section?.names?.fr
    });
    
    navigate('/');
  };

  const handleCategorySelect = (category: string) => {
    analytics.trackFilter('category_filter', category);
    
    setSelectedCategory(category);
    setSelectedType('');
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleTypeSelect = (type: string) => {
    analytics.trackFilter('type_filter', type);
    
    const itemWithType = items.find(item => item.type === type);
    if (itemWithType) {
      setSelectedCategory(itemWithType.category);
    }
    setSelectedType(type);
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      section_id: section?.id,
      section_name: section?.names?.fr,
      section_type: 'event',
      view_type: newView,
      previous_view: view
    });
    
    setView(newView);
  };

  // Create a unique URL for an event that includes date and time
  const createEventUrl = (event: EventItem): string => {
    if (!section?.names?.fr) return '';
    
    const sectionSlug = slugify(section.names.fr);
    const nameSlug = slugify(event.name);
    const dateSlug = event.date.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
    const timeSlug = event.hour.replace(':', ''); // Convert HH:MM to HHMM
    
    // Use relative navigation
    return `/${sectionSlug}/${nameSlug}-${dateSlug}-${timeSlug}`;
  };

  const handleItemClick = (item: EventItem) => {
    // Save current scroll position before navigating
    scrollPositionRef.current = window.scrollY;
    
    analytics.trackItemAction(item.id, 'item_click', 'event', {
      item_name: item.name,
      category: item.category,
      type: item.type,
      source: view === 'list' ? 'list_view' : 'map_view',
      date: item.date,
      time: item.hour,
      show_id: item.idShow || item.idshow
    });
    
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(item);
    navigate(eventUrl, { replace: true });
    
    setSelectedItem(item);
    setDetailBehavior('default');
    setSelectedPlace(null);
  };

  const handlePlaceClick = (place: Place) => {
    analytics.trackEvent('interaction', 'place_card_view', {
      section_id: section?.id,
      place_name: place.name,
      shows_count: place.shows.length,
      show_ids: place.shows.map(show => show.idShow || '').filter(Boolean)
    });
    
    setSelectedPlace(place);
    setSelectedItem(null);
  };

  const handleEventFromPlace = (event: EventItem, showIndex: number) => {
    if (!section?.names?.fr) return;

    analytics.trackItemAction(event.id, 'item_click', 'event', {
      item_name: event.name,
      category: event.category,
      type: event.type,
      source: 'place_card',
      place_name: selectedPlace?.name,
      show_index: showIndex,
      date: event.date,
      time: event.hour,
      show_id: event.idShow || event.idshow
    });
    
    const eventUrl = createEventUrl(event);
    navigate(eventUrl, { replace: true });
    
    setSelectedItem(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
  };

  const handleItemChange = (newItem: EventItem) => {
    if (!section?.names?.fr) return;
    
    analytics.trackItemAction(newItem.id, 'navigation_next', 'event', {
      item_name: newItem.name,
      category: newItem.category,
      type: newItem.type,
      previous_item_id: selectedItem?.id,
      previous_item_name: selectedItem?.name,
      date: newItem.date,
      time: newItem.hour,
      show_id: newItem.idShow || newItem.idshow
    });
    
    // Create a unique URL that includes date and time
    const eventUrl = createEventUrl(newItem);
    
    if (detailBehavior === 'place' && selectedPlace) {
      // Find the new index in the shows
      const newIndex = selectedPlace.shows.findIndex(show => 
        show.events.some(e => e.id === newItem.id)
      );
      if (newIndex !== -1) {
        setSelectedItem(newItem);
        setSelectedShowIndex(newIndex);
      }
    } else {
      setSelectedItem(newItem);
    }
    navigate(eventUrl, { replace: true });
  };

  const handleDetailClose = () => {
    // Navigate to the section without item parameter
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`, { replace: true });
    
    analytics.trackEvent('interaction', 'detail_card_close', {
      section_id: section?.id,
      item_id: selectedItem?.id,
      item_name: selectedItem?.name,
      view_duration: selectedItem ? Date.now() - scrollPositionRef.current : 0
    });
    
    setSelectedItem(null);
    setSelectedPlace(null);
    setSelectedShowIndex(null);
    setDetailBehavior('default');
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 50);
  };

  // Handle date search
  const handleSearch = () => {
    if (!startDate || !endDate) return;

    // For regular events, use local timezone - DO NOT CONVERT TO UTC
    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);

    // If start date is today, use current time
    const now = new Date();
    if (startDateTime.toDateString() === now.toDateString()) {
      startDateTime.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    } else {
      startDateTime.setHours(0, 0, 1);
    }

    // Make sure the end date includes the full day by setting it to 23:59:59
    endDateTime.setHours(23, 59, 59);

    // Save the dates to the stay service
    stayService.setStayDates(startDate, endDate);
    
    // Reset the expired flag since we have new dates
    setAreDatesExpired(false);

    // Create strings in local timezone format (YYYY-MM-DDTHH:MM:SS)
    // This is critical - we need to keep the local timezone for the event section
    const localStartDateTime = startDateTime.toISOString().replace('Z', '');
    const localEndDateTime = endDateTime.toISOString().replace('Z', '');

    // Set search params to trigger the query - use local timezone strings
    setSearchParams({
      startDateTime: localStartDateTime,
      endDateTime: localEndDateTime
    });

    // Close the date range selector modal after search
    setIsDateRangeSelectorModalOpen(false);

    // Track search event
    analytics.trackFilter('date_filter', `${startDate.toISOString()}_${endDate.toISOString()}`, {
      start_date: localStartDateTime,
      end_date: localEndDateTime
    });
  };

  // Loading state
  if (configLoading || translationsLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section) {
    analytics.trackError('section_load_error', itemsError || new Error('Missing configuration'), {
      section_id: section?.id,
      section_type: 'event'
    });
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary 
      componentName="EventSection" 
      sectionId={section.id}
      sectionName={section.names.fr}
    >
      <div 
        className="min-h-screen"
        style={{ 
          backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
        }}
      >
        <SectionHeader
          config={config}
          title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
          onBackClick={handleBackClick}
          translations={translations}
        />

        {/* Filter */}
        <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
          <CategoryFilter
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            onCategoryChange={handleCategorySelect}
            onTypeChange={handleTypeSelect}
            config={config}
            translations={translations}
            items={items}
          />
        </div>

        {/* Main content */}
        <main className="main-content">
          <div className="pt-4">
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onSearch={handleSearch}
              config={config}
              translations={translations}
              isModalOpen={isDateRangeSelectorModalOpen}
              setIsModalOpen={setIsDateRangeSelectorModalOpen}
            />
          </div>

          {searchParams === null || areDatesExpired ? (
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-gray-500">
              <p className="text-center mb-4">
                Veuillez sélectionner une période pour voir les événements
              </p>
            </div>
          ) : itemsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingIndicator />
            </div>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500 text-center">
              <p>Aucun événement disponible pour cette période</p>
            </div>
          ) : view === 'list' ? (
            <ListView
              items={filteredItems}
              onItemClick={handleItemClick}
              onCategorySelect={handleCategorySelect}
              onTypeSelect={handleTypeSelect}
              config={config}
              translations={translations}
              categoryColors={categoryColors}
            />
          ) : (
            <div 
              className="fixed inset-0 pt-[94px] pb-[40px]"
              style={{ zIndex: 1 }}
            >
              <MapView
                places={places}
                config={config}
                translations={translations}
                onItemClick={handleItemClick}
                onPlaceClick={handlePlaceClick}
                mainLocation={{
                  name: config.name,
                  latitude: config.latitude,
                  longitude: config.longitude,
                  fulladress: config.fulladress
                }}
                categoryColors={categoryColors}
                onCategorySelect={handleCategorySelect}
                onTypeSelect={handleTypeSelect}
                mapInstanceRef={mapInstanceRef}
              />
            </div>
          )}
        </main>

        {/* Fixed view toggle button */}
        <div 
          className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
        >
          <ViewToggle
            view={view}
            onChange={handleViewChange}
            config={config}
            translations={translations}
          />
        </div>

        {/* Modals */}
        {selectedPlace && !selectedItem && (
          <PlaceCard
            place={selectedPlace}
            onClose={() => {
              analytics.trackEvent('interaction', 'place_card_close', {
                place_name: selectedPlace.name,
                items_count: selectedPlace.items?.length || selectedPlace.shows?.length
              });
              setSelectedPlace(null);
            }}
            onEventClick={handleEventFromPlace}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
            selectedDate=""
          />
        )}

        {selectedItem && (
          <DetailCard
            item={selectedItem}
            items={filteredItems}
            onClose={handleDetailClose}
            onItemChange={handleItemChange}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            tableId={section.id || ''}
            categoryColors={categoryColors}
            behavior={detailBehavior}
            showIndex={selectedShowIndex}
            place={selectedPlace}
            onShowPlace={(place) => {
              setSelectedItem(null);
              setSelectedPlace(place);
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default EventSection;
export { EventSection };